import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Home, Heart, Ear, ArrowRight } from "lucide-react";

/**
 * Ken Burns + crossfade reel for the home-visit story.
 * Purely CSS/JS — no MP4. Slides auto-advance every 4s.
 *
 * To swap images, drop files in /app/frontend/public and edit the SLIDES
 * array below (path + short caption + caption icon).
 */

const SLIDES = [
    {
        src: "/home-visit-1.jpg",
        alt: "Consulta domiciliar com afeto — Dr. Marcos com paciente idosa",
        caption: "Cuidado que abraça",
        subcaption: "Estar presente ao lado de quem precisa — em casa, com afeto e escuta.",
        icon: Heart,
        zoomFrom: 1.02,
        zoomTo: 1.12,
        panX: -2,
        panY: -2,
    },
    {
        src: "/home-visit-2.jpg",
        alt: "Atendimento domiciliar com casal idoso e família",
        caption: "Consulta na sua casa",
        subcaption: "O cuidado que vai até onde você está — com sua família por perto.",
        icon: Home,
        zoomFrom: 1.06,
        zoomTo: 1.14,
        panX: 2,
        panY: 3,
    },
    {
        src: "/dr-marcos.jpg",
        alt: "Dr. Marcos Rodrigo Fernandes",
        caption: "Escuta atenta a cada história",
        subcaption: "Medicina de família e geriatria com tempo, propósito e ciência.",
        icon: Ear,
        zoomFrom: 1.05,
        zoomTo: 1.10,
        panX: 0,
        panY: -1,
    },
];

const SLIDE_DURATION_MS = 4500;
const TRANSITION_MS = 900;

export default function HomeVisitReel() {
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        const t = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), SLIDE_DURATION_MS);
        return () => clearInterval(t);
    }, []);

    return (
        <section
            className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14"
            data-testid="landing-home-visit-reel"
        >
            <div className="grid lg:grid-cols-5 gap-10 items-center">
                {/* Left — reel */}
                <div className="lg:col-span-3 order-2 lg:order-1">
                    <div className="relative rounded-3xl overflow-hidden shadow-xl bg-brand aspect-[16/10]">
                        {/* Slides */}
                        {SLIDES.map((s, i) => (
                            <div
                                key={i}
                                className="absolute inset-0 transition-opacity duration-[900ms] ease-in-out"
                                style={{ opacity: i === idx ? 1 : 0 }}
                                data-testid={`reel-slide-${i}`}
                            >
                                <img
                                    src={s.src}
                                    alt={s.alt}
                                    loading={i === 0 ? "eager" : "lazy"}
                                    className="absolute inset-0 w-full h-full object-cover"
                                    style={{
                                        transform: i === idx
                                            ? `scale(${s.zoomTo}) translate(${s.panX}%, ${s.panY}%)`
                                            : `scale(${s.zoomFrom}) translate(0%, 0%)`,
                                        transition: `transform ${SLIDE_DURATION_MS + TRANSITION_MS}ms ease-out`,
                                    }}
                                />
                                {/* Gradient scrim so captions are always legible */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                            </div>
                        ))}

                        {/* Caption layer — animates in with each slide */}
                        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 text-white">
                            {SLIDES.map((s, i) => {
                                const Icon = s.icon;
                                return (
                                    <div
                                        key={i}
                                        className="absolute inset-x-0 bottom-0 p-6 sm:p-8 transition-all duration-700"
                                        style={{
                                            opacity: i === idx ? 1 : 0,
                                            transform: i === idx ? "translateY(0)" : "translateY(12px)",
                                        }}
                                    >
                                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-[11px] font-semibold uppercase tracking-widest mb-3">
                                            <Icon size={12} /> Consulta domiciliar
                                        </div>
                                        <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold leading-tight max-w-2xl">
                                            {s.caption}
                                        </h3>
                                        <p className="mt-2 text-sm sm:text-base opacity-90 max-w-lg">
                                            {s.subcaption}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Slide indicators */}
                        <div className="absolute top-4 right-4 flex gap-1.5">
                            {SLIDES.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setIdx(i)}
                                    aria-label={`Ir para slide ${i + 1}`}
                                    data-testid={`reel-dot-${i}`}
                                    className={`h-1.5 rounded-full transition-all duration-500 ${
                                        i === idx ? "w-8 bg-white" : "w-4 bg-white/40 hover:bg-white/70"
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right — copy */}
                <div className="lg:col-span-2 order-1 lg:order-2">
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-brand-secondary mb-2">
                        Consulta na sua casa
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl font-semibold text-brand leading-tight mb-4">
                        O cuidado que vai <span className="text-[var(--brand-accent)]">até você</span>
                    </h2>
                    <p className="text-base text-[var(--text-secondary)] leading-relaxed mb-6">
                        Para muitos idosos, sair de casa é desafiador. A consulta domiciliar oferece a mesma
                        rigor clínico da Avaliação Geriátrica Ampla — com a vantagem de acontecer no ambiente onde
                        a pessoa vive, com a família por perto e o tempo que cada história merece.
                    </p>
                    <ul className="space-y-3 mb-8">
                        {[
                            { icon: Home, text: "Atendimento no seu ambiente familiar" },
                            { icon: Heart, text: "Avaliação completa da saúde e do contexto de vida" },
                            { icon: Ear, text: "Escuta ativa do paciente, cuidador e família" },
                        ].map((it, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm text-[var(--text-secondary)]">
                                <span className="w-8 h-8 rounded-lg bg-alt flex items-center justify-center shrink-0">
                                    <it.icon size={16} className="text-brand" />
                                </span>
                                <span className="pt-1.5">{it.text}</span>
                            </li>
                        ))}
                    </ul>
                    <Link
                        to="/register"
                        data-testid="landing-home-visit-cta"
                        className="btn-primary"
                    >
                        Agendar consulta domiciliar <ArrowRight size={16} />
                    </Link>
                </div>
            </div>
        </section>
    );
}
